import { z } from 'zod';
import type { AssetFieldSettings } from '@/hooks/api/useOrganizationSettings';

/**
 * Build a dynamic asset schema based on organization field settings
 * This allows admins to configure which fields are required
 */
export function buildAssetSchema(fieldSettings?: AssetFieldSettings) {
  // Helper to create field schema based on settings
  const stringField = (fieldName: keyof AssetFieldSettings, label: string) => {
    const config = fieldSettings?.[fieldName];
    if (config?.required) {
      return z.string().min(1, `${label} is required`);
    }
    // Optional fields can be empty string or null
    return z.string().optional().or(z.literal(''));
  };

  return z.object({
    description: stringField('description', 'Description'),
    tag_id: stringField('tag_id', 'Asset tag'),
    brand: stringField('brand', 'Brand'),
    model: stringField('model', 'Model'),
    serial_number: stringField('serial_number', 'Serial number'),
    status: z.enum(['Available', 'Assigned', 'Maintenance', 'Retired', 'Lost']),
    cost: z.number().nullable(),
    purchase_date: stringField('purchase_date', 'Purchase date'),
    purchased_from: stringField('purchased_from', 'Vendor'),
    warranty_period: z.number().nullable(),
    site_id: z.string().nullable(),
    location_id: z.string().nullable(),
    category_id: z.string().nullable(),
    department_id: z.string().nullable(),

    // Depreciation fields
    is_depreciable: z.boolean().optional(),
    depreciable_cost: z.number().nullable().optional(),
    asset_life_months: z.number().nullable().optional(),
    salvage_value: z.number().nullable().optional(),
    depreciation_method: z
      .enum(['straight-line', 'declining-balance', 'double-declining', 'sum-of-years'])
      .nullable()
      .optional(),
    date_acquired: z.string().nullable().optional(),
  });
}

// Default static schema for backwards compatibility
export const assetSchema = buildAssetSchema();

export type AssetFormValues = z.infer<typeof assetSchema>;
